import React from 'react';
import {Box, MenuItem, Stack, Typography} from '@mui/material';
import {InputField} from '@/pages/components/common';
import {AuditProgramSectionVO} from '@/services/auditService/auditProgramService';
import {B1_CONTENT} from '@/pages/components/audit-program/b-section/constants';
import ProgramSectionHeader from '@/pages/components/audit-program/a-section/ProgramSectionHeader';
import {yellowFieldSx} from '@/pages/components/audit-program/a-section/yellowFieldSx';
import ProgramDateField from '@/pages/components/audit-program/ProgramDateField';

export interface B1MemorandumViewProps {
    header: AuditProgramSectionVO;
    values: Record<string, string>;
    onChange: (key: string, value: string) => void;
    /** Template copy; defaults to code constant when omitted. */
    content?: typeof B1_CONTENT;
}

const YES_NO = ['', 'YES', 'NO'];

function YellowInput({
    value,
    onChange,
    multiline,
    minRows,
    placeholder,
}: {
    value: string;
    onChange: (v: string) => void;
    multiline?: boolean;
    minRows?: number;
    placeholder?: string;
}) {
    return (
        <Box sx={yellowFieldSx}>
            <InputField
                type="text"
                fullWidth
                compact
                align="left"
                multiline={multiline}
                minRows={minRows}
                placeholder={placeholder}
                value={value}
                onChange={(e) => onChange(e.target.value)}
            />
        </Box>
    );
}

function YesNoSelect({value, onChange}: {value?: string; onChange: (v: string) => void}) {
    return (
        <InputField
            select
            compact
            align="left"
            value={value ?? ''}
            onChange={(e) => onChange(e.target.value)}
            sx={{minWidth: 100}}
        >
            {YES_NO.map((opt) => (
                <MenuItem key={opt || 'blank'} value={opt}>{opt || '—'}</MenuItem>
            ))}
        </InputField>
    );
}

/** Excel stores "B2.1 Completed - YES / NO"; UI shows prefix + dropdown like A1. */
function completedPrefix(label?: string): string {
    if (!label) return '';
    const trimmed = label.replace(/\s*YES\s*\/\s*NO\s*$/i, '').trim();
    return trimmed.endsWith('-') ? trimmed : `${trimmed} -`;
}

/**
 * Layout mirrors Excel sheet B1 (Completion Memorandum).
 */
export default function B1MemorandumView({header, values, onChange, content}: B1MemorandumViewProps) {
    // Prefer template from API; fall back per-field when import left blanks (e.g. bad label match).
    const copy = (() => {
        if (!content) return B1_CONTENT;
        const pick = <K extends keyof typeof B1_CONTENT>(key: K): (typeof B1_CONTENT)[K] => {
            const v = content[key];
            if (typeof v === 'string') {
                return (v.trim() ? v : B1_CONTENT[key]) as (typeof B1_CONTENT)[K];
            }
            if (key === 'followUpSections' && Array.isArray(v) && v.length > 0) {
                const defaults = B1_CONTENT.followUpSections;
                const merged = (v as Array<Record<string, unknown>>).map((sec) => {
                    const keyId = String(sec.key ?? '');
                    const fallback = defaults.find((d) => d.key === keyId);
                    return {
                        ...fallback,
                        ...sec,
                        completedLabel:
                            (typeof sec.completedLabel === 'string' && sec.completedLabel.trim())
                                ? sec.completedLabel
                                : fallback && 'completedLabel' in fallback
                                    ? fallback.completedLabel
                                    : undefined,
                        defaultText:
                            (typeof sec.defaultText === 'string' && sec.defaultText.trim())
                                ? sec.defaultText
                                : fallback && 'defaultText' in fallback
                                    ? fallback.defaultText
                                    : undefined,
                    };
                });
                return merged as unknown as (typeof B1_CONTENT)[K];
            }
            if (Array.isArray(v) && v.length > 0) {
                return v as (typeof B1_CONTENT)[K];
            }
            return B1_CONTENT[key];
        };
        return {
            ...B1_CONTENT,
            ...content,
            title: pick('title'),
            intro: pick('intro'),
            section1Title: pick('section1Title'),
            confirmIntro: pick('confirmIntro'),
            confirmItems: pick('confirmItems'),
            noteAfter9: pick('noteAfter9'),
            authorizeText: pick('authorizeText'),
            partnerLabel: pick('partnerLabel'),
            secondPartnerSectionTitle: pick('secondPartnerSectionTitle'),
            secondPartnerConfirmIntro: pick('secondPartnerConfirmIntro'),
            signOffByLabel: pick('signOffByLabel'),
            secondPartnerLabel: pick('secondPartnerLabel'),
            secondPartnerItems: pick('secondPartnerItems'),
            followUpSections: pick('followUpSections'),
        };
    })();
    return (
        <Box sx={{p: 2, maxWidth: 960}}>
            <ProgramSectionHeader header={header} variant="a1" refCode="B1" values={values} onChange={onChange}/>

            <Typography
                variant="subtitle1"
                fontWeight={700}
                sx={{textAlign: 'center', letterSpacing: 0.5, mb: 1.5, mt: 1}}
            >
                {copy.title}
            </Typography>

            <Typography variant="body2" sx={{mb: 2, lineHeight: 1.5}}>
                {copy.intro}
            </Typography>

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.section1Title}
            </Typography>
            <Typography variant="body2" sx={{mb: 1}}>
                {copy.confirmIntro}
            </Typography>
            <Stack spacing={0.75} sx={{mb: 2, pl: 0.5}}>
                {copy.confirmItems.map((item) => (
                    <React.Fragment key={item.num}>
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Typography variant="body2" sx={{minWidth: 22, fontWeight: 600}}>{item.num}</Typography>
                            <Typography variant="body2" sx={{lineHeight: 1.45, whiteSpace: 'pre-line'}}>
                                {item.text}
                            </Typography>
                        </Stack>
                        {item.num === '9' && copy.noteAfter9 ? (
                            <Typography variant="caption" color="text.secondary" sx={{pl: 4.5, display: 'block'}}>
                                {copy.noteAfter9}
                            </Typography>
                        ) : null}
                        {item.num === '11' ? (
                            <Box sx={{pl: 4.5, mb: 1}}>
                                <YellowInput
                                    value={values.workBeforeSign ?? ''}
                                    onChange={(v) => onChange('workBeforeSign', v)}
                                    multiline
                                    minRows={2}
                                    placeholder="Nil"
                                />
                            </Box>
                        ) : null}
                    </React.Fragment>
                ))}
            </Stack>

            <Typography variant="body2" sx={{mb: 2}}>
                {copy.authorizeText}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 160px'},
                    columnGap: 4,
                    rowGap: 0.5,
                    mb: 3,
                    alignItems: 'start',
                }}
            >
                <Box>
                    <YellowInput
                        value={values.partnerName ?? header.partnerName ?? ''}
                        onChange={(v) => onChange('partnerName', v)}
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{display: 'block', mt: 0.25, lineHeight: 1.25}}
                    >
                        {copy.partnerLabel}
                    </Typography>
                </Box>
                <ProgramDateField
                    value={values.partnerDate ?? ''}
                    onChange={(v) => onChange('partnerDate', v)}
                />
            </Box>

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.secondPartnerSectionTitle}
            </Typography>
            <Typography variant="body2" sx={{mb: 1}}>
                {copy.secondPartnerConfirmIntro}
            </Typography>
            <Stack spacing={0.75} sx={{mb: 2, pl: 0.5}}>
                {copy.secondPartnerItems.map((item) => (
                    <Stack key={item.num} direction="row" spacing={1.5} alignItems="flex-start">
                        <Typography variant="body2" sx={{minWidth: 22, fontWeight: 600}}>{item.num}</Typography>
                        <Typography variant="body2" sx={{lineHeight: 1.45}}>{item.text}</Typography>
                    </Stack>
                ))}
            </Stack>

            <Typography variant="body2" fontWeight={700} sx={{mb: 1}}>
                {copy.signOffByLabel}
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: {xs: '1fr', sm: '1fr 160px'},
                    columnGap: 4,
                    rowGap: 0.5,
                    mb: 3,
                    alignItems: 'start',
                }}
            >
                <Box>
                    <YellowInput
                        value={values.secondPartnerName ?? ''}
                        onChange={(v) => onChange('secondPartnerName', v)}
                    />
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{display: 'block', mt: 0.25, lineHeight: 1.25}}
                    >
                        {copy.secondPartnerLabel}
                    </Typography>
                </Box>
                <ProgramDateField
                    value={values.secondPartnerDate ?? ''}
                    onChange={(v) => onChange('secondPartnerDate', v)}
                />
            </Box>

            {copy.followUpSections.map((section) => {
                const prefix = completedPrefix(
                    'completedLabel' in section ? section.completedLabel : undefined,
                );
                const completedKey = `${section.key}Completed`;
                return (
                    <Box key={section.key} sx={{mb: 3}}>
                        <Stack
                            direction={{xs: 'column', sm: 'row'}}
                            justifyContent="space-between"
                            alignItems={{xs: 'flex-start', sm: 'center'}}
                            spacing={1}
                            sx={{mb: 1}}
                        >
                            <Typography variant="body2" fontWeight={700}>
                                {section.title}
                            </Typography>
                            {prefix ? (
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography variant="body2" color="text.secondary" sx={{whiteSpace: 'nowrap'}}>
                                        {prefix}
                                    </Typography>
                                    <YesNoSelect
                                        value={values[completedKey]}
                                        onChange={(v) => onChange(completedKey, v)}
                                    />
                                </Stack>
                            ) : null}
                        </Stack>
                        <YellowInput
                            value={values[section.key] ?? ''}
                            onChange={(v) => onChange(section.key, v)}
                            multiline
                            minRows={2}
                            placeholder="N/A"
                        />
                    </Box>
                );
            })}
        </Box>
    );
}
